import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Zap,
  DollarSign,
  TrendingUp,
  Users,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Search,
  RefreshCw,
  Loader2,
  Building2,
  Mail,
  Percent,
  Receipt,
  Wallet,
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import AdminStripeConnectDetailModal from "./AdminStripeConnectDetailModal";
import AdminPlatformFeePanel from "./AdminPlatformFeePanel";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
};

const StatusBadge = ({ account }) => {
  if (account.disabled_reason) {
    return (
      <Badge className="bg-red-500/15 text-red-400 border-red-500/30 gap-1">
        <AlertTriangle className="w-3 h-3" />
        Restricted
      </Badge>
    );
  }
  if (account.onboarding_complete) {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 gap-1">
        <CheckCircle2 className="w-3 h-3" />
        Active
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500/15 text-amber-500 border-amber-500/30 gap-1">
      <Clock className="w-3 h-3" />
      Pending
    </Badge>
  );
};

const CapPill = ({ enabled, label, testId }) => (
  <span
    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border ${
      enabled
        ? "border-emerald-500/30 text-emerald-500/90 bg-emerald-500/5"
        : "border-border text-muted-foreground bg-muted/20"
    }`}
    data-testid={testId}
  >
    {label}
  </span>
);

const FeeModelPill = ({ feeModel, testId }) => (
  <span
    className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-md border whitespace-nowrap ${
      feeModel === "merchant_pays"
        ? "border-emerald-500/30 text-emerald-500 bg-emerald-500/5"
        : "border-amber-500/30 text-amber-500 bg-amber-500/5"
    }`}
    data-testid={testId}
  >
    {feeModel === "merchant_pays" ? "1% Direct" : "Legacy 3.8%"}
  </span>
);

const StatCard = ({ icon: Icon, label, value, color, testId }) => (
  <Card className="glass-card border-theme" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl ${color.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color.text}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-lg font-bold text-foreground truncate">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

const AdminStripeConnect = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  // Per-merchant drill-down modal — opens when a row is clicked.
  const [drilldownUserId, setDrilldownUserId] = useState(null);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 100 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (search) params.search = search;
      const res = await axios.get(`${API}/admin/stripe-connect/accounts`, {
        ...getAuthConfig(),
        params,
      });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load connected accounts");
      setData({ totals: {}, accounts: [] });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sort, search]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // debounce search by 400ms
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totals = data?.totals || {};
  const accounts = data?.accounts || [];

  return (
    <div className="space-y-6" data-testid="admin-stripe-connect-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope'] flex items-center gap-2">
            <Zap className="w-6 h-6 text-[#635BFF]" />
            Stripe Connect Accounts
          </h1>
          <p className="text-muted-foreground text-sm">
            Every merchant who connected their own Stripe account, with platform-fee revenue accrued.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchAccounts}
          disabled={loading}
          className="gap-2"
          data-testid="admin-stripe-connect-refresh-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </Button>
      </div>

      {/* Platform Processing Fee — source of truth, verification & audit */}
      <AdminPlatformFeePanel />

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={Users}
          label="Total Connected"
          value={totals.total_accounts ?? 0}
          color={{ bg: "bg-blue-500/10", text: "text-blue-400" }}
          testId="admin-connect-stat-total"
        />
        <StatCard
          icon={CheckCircle2}
          label="Active"
          value={totals.active_accounts ?? 0}
          color={{ bg: "bg-emerald-500/10", text: "text-emerald-500" }}
          testId="admin-connect-stat-active"
        />
        <StatCard
          icon={Clock}
          label="Pending"
          value={totals.pending_accounts ?? 0}
          color={{ bg: "bg-amber-500/10", text: "text-amber-500" }}
          testId="admin-connect-stat-pending"
        />
        <StatCard
          icon={Percent}
          label="Fee Structure"
          value={`${totals.direct_accounts ?? 0} direct · ${totals.legacy_accounts ?? 0} legacy`}
          color={{ bg: "bg-violet-500/10", text: "text-violet-400" }}
          testId="admin-connect-stat-fee-models"
        />
        <StatCard
          icon={TrendingUp}
          label="Total Volume"
          value={fmtMoney(totals.total_volume)}
          color={{ bg: "bg-cyan-500/10", text: "text-cyan-400" }}
          testId="admin-connect-stat-volume"
        />
        <StatCard
          icon={DollarSign}
          label="Fees Collected (gross)"
          value={fmtMoney(totals.total_app_fees)}
          color={{ bg: "bg-[#FFCC00]/10", text: "text-[#FFCC00]" }}
          testId="admin-connect-stat-fees"
        />
        <StatCard
          icon={Receipt}
          label={`Stripe Costs (legacy${totals.stripe_cost_estimated_txns ? `, ~${totals.stripe_cost_estimated_txns} est.` : ""})`}
          value={`−${fmtMoney(totals.stripe_costs)}`}
          color={{ bg: "bg-red-500/10", text: "text-red-400" }}
          testId="admin-connect-stat-stripe-costs"
        />
        <StatCard
          icon={Wallet}
          label="Net Platform Revenue"
          value={fmtMoney(totals.net_revenue)}
          color={{ bg: "bg-emerald-500/10", text: "text-emerald-500" }}
          testId="admin-connect-stat-net"
        />
      </div>

      {/* Filters */}
      <Card className="glass-card border-theme">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by email, business name…"
              className="pl-9"
              data-testid="admin-connect-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="admin-connect-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="restricted">Restricted</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[180px]" data-testid="admin-connect-sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Most recent</SelectItem>
              <SelectItem value="top_fees">Top platform fees</SelectItem>
              <SelectItem value="top_volume">Top volume</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Accounts list */}
      <Card className="glass-card border-theme">
        <CardHeader>
          <CardTitle className="text-base font-['Manrope']">
            Connected Merchants
            {!loading && (
              <span className="text-muted-foreground text-sm font-normal ml-2">
                ({data?.returned || 0}{data?.total > data?.returned ? ` of ${data.total}` : ""})
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : accounts.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="admin-connect-empty">
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No merchants have connected a Stripe account yet.</p>
              <p className="text-xs mt-1">
                Once your customers complete the Stripe Connect flow in Settings → Stripe Connect, they'll appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground text-left">
                    <th className="font-medium pb-2 pr-3">Merchant</th>
                    <th className="font-medium pb-2 pr-3">Status</th>
                    <th className="font-medium pb-2 pr-3">Fee Model</th>
                    <th className="font-medium pb-2 pr-3">Capabilities</th>
                    <th className="font-medium pb-2 pr-3 text-right">Volume</th>
                    <th className="font-medium pb-2 pr-3 text-right">Fees (gross)</th>
                    <th className="font-medium pb-2 pr-3 text-right">Net to You</th>
                    <th className="font-medium pb-2 pr-3 text-right">Txns</th>
                    <th className="font-medium pb-2 pr-3">Connected</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((acc) => (
                    <tr
                      key={acc.user_id}
                      className="border-b border-border/40 hover:bg-muted/20 cursor-pointer"
                      data-testid={`admin-connect-row-${acc.user_id}`}
                      onClick={() => setDrilldownUserId(acc.user_id)}
                    >
                      <td className="py-3 pr-3 min-w-[200px]">
                        <div className="flex items-start gap-2">
                          <div className="w-8 h-8 rounded-lg bg-[#635BFF]/10 flex items-center justify-center shrink-0">
                            <Building2 className="w-4 h-4 text-[#635BFF]" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {acc.business_name || acc.name || "Unnamed merchant"}
                            </p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3" />
                              {acc.email}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground/60 mt-0.5 truncate">
                              {acc.stripe_account_id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <StatusBadge account={acc} />
                        {acc.disabled_reason && (
                          <p className="text-[10px] text-red-400 mt-1 max-w-[140px]">
                            {acc.disabled_reason}
                          </p>
                        )}
                      </td>
                      <td className="py-3 pr-3">
                        <FeeModelPill feeModel={acc.fee_model} testId={`admin-connect-feemodel-${acc.user_id}`} />
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex flex-wrap gap-1">
                          <CapPill
                            enabled={acc.charges_enabled}
                            label="Charges"
                            testId={`admin-connect-charges-${acc.user_id}`}
                          />
                          <CapPill
                            enabled={acc.payouts_enabled}
                            label="Payouts"
                            testId={`admin-connect-payouts-${acc.user_id}`}
                          />
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-right font-mono">
                        {fmtMoney(acc.total_volume)}
                      </td>
                      <td className="py-3 pr-3 text-right font-mono text-[#FFCC00]" data-testid={`admin-connect-fees-${acc.user_id}`}>
                        {fmtMoney(acc.total_app_fees)}
                      </td>
                      <td className="py-3 pr-3 text-right font-mono text-emerald-500" data-testid={`admin-connect-net-${acc.user_id}`}>
                        {fmtMoney(acc.net_revenue)}
                      </td>
                      <td className="py-3 pr-3 text-right font-mono">
                        {acc.transaction_count}
                      </td>
                      <td className="py-3 pr-3 text-muted-foreground">
                        {fmtDate(acc.connected_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-merchant drill-down modal — opens when a row is clicked.
          After a successful Resync, we refetch the list so updated status
          flags are reflected in the table immediately. */}
      <AdminStripeConnectDetailModal
        userId={drilldownUserId}
        open={!!drilldownUserId}
        onOpenChange={(o) => { if (!o) setDrilldownUserId(null); }}
        onAfterResync={fetchAccounts}
      />
    </div>
  );
};

export default AdminStripeConnect;
